import React, { useCallback, useMemo, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { useOtpSMSFlow, useReimbursementVerification } from '../../../hooks';

import checkIcon
from '../../../assets/myAccount/reimbursement/check-payment-logo.svg';
import savingBankLogo
from '../../../assets/myAccount/reimbursement/saving-bank-logo.svg';
import addIcon from '../../../assets/myPets/add-icon-blue.svg';

import {
  closeEditReimbursementMethodModal,
  openAddReimbursementMethodModal,
  sendVerificationCode,
} from '../../../actions';

import { ContactUsModal } from '../../sideMenu/contactUs/ContactUsModal';
import {
  ReimbursementMethodErrorModal,
  ReimbursementEditModal,
} from './editModal';
import { UpdateReimbursementModal } from './UpdateReimbursementModal';
import FigoLottie from '../../common/FigoLottie';

import { REIMBURSEMENT_METHODS } from '../../../constants';
import { CONTACT_US_STEPS } from '../../sideMenu/contactUs/contactUs.constants';

import { ModalContainer } from '../../common';
import { ModalOtpVerification } from '../../common/otp/ModalOtpVerification';

import styles from './css/ReimbursementMethodSection.module.css';

const ReimbursementMethodSection = ({ reimbursementOption }) => {
  const { t } = useTranslation('myAccount');
  const dispatch = useDispatch();
  const store = useSelector(({
    billingPayments,
    otpVerification,
    personalInformation,
    policies,
  }) => ({
    billingPayments,
    otpVerification,
    personalInformation,
    policies,
  }), shallowEqual);
  const {
    billingPayments: {
      addReimbursementMethodModalVisible,
      editReimbursementMethodModalVisible,
      loadingReimbursementMethod,
      reimbursementMethodUpdateError,
    },
    otpVerification: { verificationCode: {
      showModalOtp,
      verified,
      verificationToken,
    } },
    personalInformation: { userInfo },
  } = store;

  const useSmsValidation = useOtpSMSFlow();
  const useReimVerification = useReimbursementVerification();
  const [
    updateReimbursementModalVisible,
    setUpdateReimbursementModalVisible,
  ] = useState(false);
  const [contactUsVisible, setContactUsVisible] = useState(false);

  const isCheck = reimbursementOption.PreferredReimbursementMethod
    === REIMBURSEMENT_METHODS.check;

  function handleEditReimbursementCancel() {
    dispatch(closeEditReimbursementMethodModal());
  }

  const phoneNumber = useMemo(() => userInfo?.PhoneNumber
    || '', [userInfo]);

  const email = useMemo(() => userInfo?.LoginEmail
    || '', [userInfo]);

  const hideUpdateReimbursementModal = () => {
    setUpdateReimbursementModalVisible(false);
  };

  const onContactUsClick = () => {
    setContactUsVisible(true);
  };

  const onContactUsClose = () => {
    setContactUsVisible(false);
  };

  const handleChangeReimbursementClick = useCallback(() => {
    if (!useReimVerification) {
      dispatch(openAddReimbursementMethodModal(REIMBURSEMENT_METHODS.check));
      return;
    }

    // If already verified, open the modal directly
    if (verified && verificationToken) {
      dispatch(openAddReimbursementMethodModal(REIMBURSEMENT_METHODS.check));
      return;
    }

    // If SMS validation is required but no phone number
    if (useSmsValidation && !phoneNumber) {
      setUpdateReimbursementModalVisible(true);
      return;
    }

    dispatch(sendVerificationCode({ email, phoneNumber }));
  }, [
    dispatch,
    email,
    phoneNumber,
    useReimVerification,
    useSmsValidation,
    verificationToken,
  ]);

  function renderReimbursementCard() {
    if (loadingReimbursementMethod) {
      return (
        <div className={styles.lottieContainer}>
          <FigoLottie />
        </div>
      );
    }

    if (reimbursementOption.PreferredReimbursementMethod
      === REIMBURSEMENT_METHODS.none) {
      return (
        <button
          className={styles.addButton}
          onClick={handleChangeReimbursementClick}
          type="button"
        >
          <img alt="" src={addIcon} />

          <span>{t('reimbursementSection.addReimbursementMethod')}</span>
        </button>
      );
    }

    return (
      <div className={styles.cardContainer}>
        <div className={styles.cardLeftContent}>
          <div className={styles.reimbursementDetail}>
            <img alt="" src={isCheck ? checkIcon : savingBankLogo} />

            <p>
              {isCheck ? t('reimbursementSection.checkMethod', {
                addressLine1: reimbursementOption.Details.AddressLine1,
                addressLine2: reimbursementOption.Details.AddressLine2,
                stateId: reimbursementOption.Details.State,
                zipCode: reimbursementOption.Details.Zipcode,
              }) : t('reimbursementSection.bankAccount', {
                accountNumberLastFour:
                  reimbursementOption.AccountNumberLastFour,
              })}
            </p>
          </div>
        </div>

        <div className={styles.actions}>
          <button
            className={styles.editButton}
            onClick={handleChangeReimbursementClick}
            type="button"
          >
            <span>{t('reimbursementSection.editOptions.title')}</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.sectionContainer}>
      <h1>{t('reimbursementSection.reimbursementMethod')}</h1>

      <p>{t('reimbursementSection.reimbursmentDescription')}</p>

      {renderReimbursementCard()}

      <ReimbursementEditModal
        isEditing={editReimbursementMethodModalVisible}
        isVisible={editReimbursementMethodModalVisible
          || addReimbursementMethodModalVisible}
        onCancel={handleEditReimbursementCancel}
      />

      {reimbursementMethodUpdateError && <ReimbursementMethodErrorModal />}

      {useReimVerification ? (
        <ModalContainer
          cancelable={false}
          show={showModalOtp && !verified}
        >
          <ModalOtpVerification />
        </ModalContainer>
      ) : null}

      <UpdateReimbursementModal
        onButtonClick={onContactUsClick}
        onClose={hideUpdateReimbursementModal}
        show={updateReimbursementModalVisible}
      />

      <ContactUsModal
        companyStep={CONTACT_US_STEPS.HOME}
        onClose={onContactUsClose}
        show={contactUsVisible}
      />
    </div>
  );
};

export { ReimbursementMethodSection };
